import { useQuery } from '@tanstack/react-query';
import ApiService from './api.service';

export interface ReportBuilderDimension {
  id: string;
  name: string;
  pipeline_node_id: string;
  field_id: string;
  description: string;
  column_name: string;
}

export interface ReportBuilderDimensionJoinTree {
  pipeline_node_id: string;
  join_tree: any | null;
  label?: string;
  ignore?: boolean;
  custom_path?: any;
}

export interface ReportBuilderMeasure {
  id: string;
  name: string;
  description: string;
  column_name: string;
  is_calculation?: boolean;
  pipeline_node_id?: string;
  field_id?: string;
  aggregator?: string;
  formatter?: string;
  data_whitelist?: any;
  dimension_join_trees?: ReportBuilderDimensionJoinTree[];
  formula?: string;
  parent_measure_ids?: string[];
  metric_type?: 'BASIC' | 'CALCULATED' | 'COHORT';
  activity_measure_id?: string;
  diagram_x?: number;
  diagram_y?: number;
}

export type ListRecordsResponse<T> = {
  records: T[];
  total_pages?: number;
  total_records?: number;
};

export class ReportBuilderService {
  async findDimensions(): Promise<ReportBuilderDimension[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/dimensions'
    ) as ListRecordsResponse<ReportBuilderDimension>;
    return result.records;
  }

  async findMeasures(): Promise<ReportBuilderMeasure[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/measures'
    ) as ListRecordsResponse<ReportBuilderMeasure>;
    return result.records;
  }

  async findDimensionById(id: string): Promise<ReportBuilderDimension> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/dimensions/${id}`
    ) as { record: ReportBuilderDimension };
    return result.record;
  }

  async findMeasureById(id: string): Promise<ReportBuilderMeasure> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/measures/${id}`
    ) as { record: ReportBuilderMeasure };
    return result.record;
  }
}

export const reportBuilderService = new ReportBuilderService();

export function useDimensions() {
  return useQuery<ReportBuilderDimension[]>({
    queryKey: ['dimensions'],
    queryFn: () => reportBuilderService.findDimensions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useMeasures() {
  return useQuery<ReportBuilderMeasure[]>({
    queryKey: ['measures'],
    queryFn: () => reportBuilderService.findMeasures(),
    staleTime: Infinity,
  });
}

export function useDimension(dimensionId: string | null) {
  return useQuery<ReportBuilderDimension | null>({
    queryKey: ['dimensions', dimensionId],
    queryFn: () => {
      if (!dimensionId) {
        return Promise.resolve(null);
      }
      return reportBuilderService.findDimensionById(dimensionId);
    },
    enabled: !!dimensionId,
  });
}

export function useMeasure(measureId: string | null) {
  return useQuery<ReportBuilderMeasure | null>({
    queryKey: ['measures', measureId],
    queryFn: () => {
      if (!measureId) {
        return Promise.resolve(null);
      }
      return reportBuilderService.findMeasureById(measureId);
    },
    enabled: !!measureId,
  });
}

export interface PipelineNode {
  id: string;
  name: string;
  node_type: string;
  taxonomic_id?: string;
}

export function useCalendarNode() {
  return useQuery<PipelineNode | null>({
    queryKey: ['calendar-node'],
    queryFn: async () => {
      try {
        // Try to fetch with filter first
        const result = await ApiService.getInstance().request(
          'GET',
          '/pipelinenodes',
          { node_type: 'DATE_DIMENSION' }
        ) as ListRecordsResponse<PipelineNode>;
        // Find the calendar node (should be the DATE_DIMENSION node)
        const calendarNode = result.records.find(n => n.node_type === 'DATE_DIMENSION');
        if (calendarNode) return calendarNode;
      } catch (error) {
        // If filtering doesn't work, fetch all and filter
        console.warn('Calendar node query with filter failed, fetching all nodes');
      }
      
      // Fallback: fetch all nodes and filter
      const result = await ApiService.getInstance().request(
        'GET',
        '/pipelinenodes'
      ) as ListRecordsResponse<PipelineNode>;
      // Find the calendar node by node_type or taxonomic_id
      return result.records.find(n => 
        n.node_type === 'DATE_DIMENSION' || 
        n.taxonomic_id === 'PLB:::TPL:DEFAULT::NODE:CALENDAR'
      ) || null;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUniqueValuesInDimension(dimensionId: string | null, query?: string) {
  return useQuery<string[]>({
    queryKey: ['dimension_values', dimensionId, query],
    queryFn: async () => {
      if (!dimensionId) {
        return [];
      }
      const params: Record<string, string> = {};
      if (query) {
        params.q = query;
      }
      const url = `/dimensions/${dimensionId}/values`;
      const result = await ApiService.getInstance().request(
        'GET',
        url,
        Object.keys(params).length > 0 ? params : undefined
      ) as ListRecordsResponse<string>;
      return result.records;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!dimensionId,
  });
}

export async function fetchDimensionValues(dimensionId: string, query?: string): Promise<string[]> {
  if (!dimensionId) {
    return [];
  }
  const params: Record<string, string> = {};
  if (query) {
    params.q = query;
  }
  const url = `/dimensions/${dimensionId}/values`;
  const result = await ApiService.getInstance().request(
    'GET',
    url,
    Object.keys(params).length > 0 ? params : undefined
  ) as ListRecordsResponse<string>;
  return result.records;
}

